import {
	faAt,
	faBell,
	faBellSlash,
	faBoxesStacked,
	faCalendarCheck,
	faCartShopping,
	faClipboardCheck,
	faComments,
	faFileInvoiceDollar,
	faGear,
	faMoneyBillWave,
	faPhone,
	faRobot,
	faTriangleExclamation,
	faXmark,
	type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatRelativeTime } from '@supply-mover-2-0/core';
import {
	NOTIFICATION_CATEGORY,
	NOTIFICATION_TYPE,
} from '@supply-mover-2-0/shared';
import type React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Toggle } from '../../components/forms/Toggle';
import { Tooltip } from '../../components/overlays/Tooltip';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { Toast } from '../../services/ToastService';
import { cn } from '../../utils/cn';
import { createSharedPoll, type SharedPoll } from '../../utils/sharedPoll';
import { registerIndicator } from '../notificationIndicatorStore';
import { NotificationQueryService } from '../services/NotificationQueryService';

/** Slow poll for the badge while the tray is closed. */
const INDICATOR_POLL_MS = 60_000;
/** Faster poll for the list while the tray is open. */
const PANEL_DATA_POLL_MS = 30_000;
/** Page size for the in-tray list, and for each older page loaded on scroll. */
const LIST_LIMIT = 30;
/**
 * The API's largest page. A refresh re-reads every row already shown (so the
 * older pages a user scrolled to don't vanish on the next poll) in requests of
 * at most this many.
 */
const MAX_LIST_REQUEST = 100;
/** Start loading the next older page this close to the bottom of the list. */
const LOAD_MORE_THRESHOLD_PX = 120;
/** Badge collapses past this. */
const BADGE_CAP = 9;

/**
 * One row the tray renders. The CRM's `UserNotificationSummaryDto` and the
 * storefront's `PortalNotificationSummaryDto` (PD-16475) both satisfy it; the
 * storefront has no priority, so it is optional.
 */
export interface NotificationsPanelItemModel {
	id: number;
	notificationType: string;
	category: string;
	priority?: string | null;
	title: string;
	body: string | null;
	entityType: string | null;
	entityId: number | null;
	isRead: boolean;
	createdDate: string;
}

/**
 * Where a bell reads and updates its notifications (PD-16475). The CRM bell
 * uses `crmNotificationsSource`; the storefront passes its own over
 * `/api/portal/notifications`, whose ids are a different user table.
 */
export interface NotificationsDataSourceModel {
	/**
	 * Identifies the source. The unread badge poll is shared per key, so two
	 * bells over the same source make one request, and two sources never mix
	 * counts.
	 */
	key: string;
	/**
	 * One page of rows. `offset` and `unreadOnly` are only passed to a source
	 * that declares `unreadOnlyPreference`; any other source is asked for the
	 * first page alone.
	 */
	list: (args: {
		limit: number;
		offset?: number;
		unreadOnly?: boolean;
	}) => Promise<ReadonlyArray<NotificationsPanelItemModel>>;
	getUnreadCount: () => Promise<number>;
	markRead: (notificationId: number) => Promise<void>;
	markAllRead: () => Promise<void>;
	/** Omit when the source cannot dismiss; the per-row × is then hidden. */
	dismiss?: (notificationId: number) => Promise<void>;
	/**
	 * Omit when the source cannot mark a notification unread. When present,
	 * the per-row unread dot becomes a read / unread toggle (PD-14374).
	 */
	markUnread?: (notificationId: number) => Promise<void>;
	/**
	 * The user's remembered "Unread only" choice (PD-14374). When present the
	 * tray shows the toggle, lists rows newest first as one list, and loads
	 * older pages as the user scrolls, so `list` must honor `offset` and
	 * `unreadOnly`. Omit it to keep the first page grouped into New / Earlier.
	 */
	unreadOnlyPreference?: {
		get: () => Promise<boolean>;
		set: (unreadOnly: boolean) => Promise<void>;
	};
}

/**
 * Colors for the tray, as CSS color values. Every field defaults to the CRM
 * theme token it replaced, so the CRM renders unchanged; the storefront passes
 * its brand variables so the tray matches its own dropdowns.
 */
export interface NotificationsPanelPaletteModel {
	surface: string;
	border: string;
	textPrimary: string;
	textSecondary: string;
	link: string;
	unreadDot: string;
	/** Ring around the count badge; match the surface the button sits on. */
	badgeRing: string;
}

/**
 * The CRM user's own notifications (`/api/notifications/me`). The default
 * source. PD-14374: a CRM notification is only ever read or unread, so this
 * source offers mark-unread and the remembered "Unread only" view, and no
 * dismiss (the × is gone from the CRM tray).
 */
export const crmNotificationsSource: NotificationsDataSourceModel = {
	key: 'crm',
	list: async ({ limit, offset, unreadOnly }) =>
		(await NotificationQueryService.list({ limit, offset, unreadOnly }))
			.notifications,
	getUnreadCount: async () =>
		(await NotificationQueryService.getUnreadCount()).count,
	markRead: async (notificationId) => {
		await NotificationQueryService.markRead(notificationId);
	},
	markAllRead: async () => {
		await NotificationQueryService.markAllRead();
	},
	markUnread: async (notificationId) => {
		await NotificationQueryService.markUnread(notificationId);
	},
	unreadOnlyPreference: {
		get: async () =>
			(await NotificationQueryService.getPreferences()).trayUnreadOnly,
		set: async (unreadOnly) => {
			await NotificationQueryService.updatePreferences({
				trayUnreadOnly: unreadOnly,
			});
		},
	},
};

/**
 * The "Unread only" choice per source, read from the server once per signed-in
 * session. Module-level because the TopBar mounts two bells (desktop and
 * mobile) and both should agree without each fetching it; cleared on logout
 * by `resetShellSessionCaches` so the next user starts from their own setting.
 */
const trayUnreadOnlyBySource = new Map<string, boolean>();

/** Forget the remembered tray views (logout). */
export function clearNotificationsTrayViewCache(): void {
	trayUnreadOnlyBySource.clear();
}

/**
 * Read `count` rows from the top of a view, in requests no larger than the
 * API allows. `hasMore` is true when the last request came back full, i.e.
 * there may be older rows still to load.
 */
async function loadLeadingRows(
	source: NotificationsDataSourceModel,
	unreadOnly: boolean,
	count: number,
): Promise<{
	rows: Array<NotificationsPanelItemModel>;
	hasMore: boolean;
}> {
	const rows: Array<NotificationsPanelItemModel> = [];
	let hasMore = true;
	while (rows.length < count && hasMore) {
		const limit = Math.min(MAX_LIST_REQUEST, count - rows.length);
		const page = await source.list({
			limit,
			offset: rows.length,
			unreadOnly,
		});
		rows.push(...page);
		hasMore = page.length === limit;
	}
	return { rows, hasMore };
}

/** Append rows not already listed (offset paging can repeat one when a new
 *  notification arrives between two page requests). */
function appendNewRows(
	existing: ReadonlyArray<NotificationsPanelItemModel>,
	page: ReadonlyArray<NotificationsPanelItemModel>,
): ReadonlyArray<NotificationsPanelItemModel> {
	const seen = new Set(existing.map((n) => n.id));
	const fresh = page.filter((n) => !seen.has(n.id));
	return fresh.length === 0 ? existing : [...existing, ...fresh];
}

/** The CRM's notification settings (PD-16112: the Notifications tab of Profile). */
const CRM_SETTINGS_PATH = '/profile?tab=notifications';

function usePanelPalette(
	overrides: Partial<NotificationsPanelPaletteModel> | undefined,
): NotificationsPanelPaletteModel {
	const colors = useThemeColors();
	return {
		surface: overrides?.surface ?? colors.values.surfaceElevated,
		border: overrides?.border ?? colors.values.borderDefault,
		textPrimary: overrides?.textPrimary ?? colors.values.textPrimary,
		textSecondary: overrides?.textSecondary ?? colors.values.textSecondary,
		link: overrides?.link ?? colors.values.link,
		unreadDot: overrides?.unreadDot ?? colors.values.primary,
		badgeRing: overrides?.badgeRing ?? colors.values.surfaceElevated,
	};
}

/**
 * Maps a notification to its icon — by the specific `notificationType`
 * first, falling back to the broad `category`, then a bell. Keeps the
 * vocabulary aligned with `@supply-mover-2-0/shared` constants.
 */
function iconForNotification(type: string, category: string): IconDefinition {
	switch (type) {
		case NOTIFICATION_TYPE.ORDER_APPROVAL_REQUIRED:
			return faClipboardCheck;
		case NOTIFICATION_TYPE.MENTION:
			return faAt;
		case NOTIFICATION_TYPE.TASK_DUE:
			// Task due / overdue reminder — a calendar icon reads as
			// "deadline" rather than falling back to the SALES cart icon.
			return faCalendarCheck;
		case NOTIFICATION_TYPE.INVOICE_OVERDUE:
			return faFileInvoiceDollar;
		case NOTIFICATION_TYPE.PAYMENT_RECEIVED:
			return faMoneyBillWave;
		case NOTIFICATION_TYPE.SYNC_FAILED:
			return faTriangleExclamation;
		default:
			break;
	}
	switch (category) {
		case NOTIFICATION_CATEGORY.PHONE:
			return faPhone;
		case NOTIFICATION_CATEGORY.SALES:
			return faCartShopping;
		case NOTIFICATION_CATEGORY.APPROVALS:
			return faClipboardCheck;
		case NOTIFICATION_CATEGORY.FINANCIAL:
			return faMoneyBillWave;
		case NOTIFICATION_CATEGORY.COLLABORATION:
			return faComments;
		case NOTIFICATION_CATEGORY.OPERATIONS:
			return faBoxesStacked;
		case NOTIFICATION_CATEGORY.SYSTEM:
			return faGear;
		case NOTIFICATION_CATEGORY.AI:
			return faRobot;
		default:
			return faBell;
	}
}

/**
 * Resolves a notification's `(entityType, entityId)` to an in-app
 * route so clicking it lands on the source. Routes verified against
 * the module configs (orders/clients/etc. all use `/<base>/:id`).
 * Returns null for system notifications with no entity (no nav). The
 * map grows as Phase B producers introduce new entity types.
 *
 * Exported for its unit test only; it is not part of the library's public
 * surface (the notifications index does not re-export it).
 */
export function resolveEntityPath(
	entityType: string | null,
	entityId: number | null,
): string | null {
	if (entityType == null || entityId == null) return null;
	switch (entityType) {
		case 'order':
			return `/orders/${entityId}`;
		case 'client':
			return `/clients/${entityId}`;
		case 'client_import':
			// PD-16927 — an import's summary (the id is the import run). It
			// opens the clients list rather than the run page: the people it
			// goes to are mostly reps, who cannot open import runs.
			return '/clients';
		case 'invoice':
			return `/invoices/${entityId}`;
		case 'vendor':
			return `/vendors/${entityId}`;
		case 'quote':
			return `/quotes/${entityId}`;
		case 'opportunity':
			return `/opportunities/${entityId}`;
		case 'purchase_order':
			return `/purchase-orders/${entityId}`;
		case 'item':
			return `/items/${entityId}`;
		case 'issue':
			return `/issues/${entityId}`;
		case 'license':
			return `/licenses/${entityId}`;
		case 'sourcing_request':
			return `/sourcing-requests/${entityId}`;
		case 'form_submission':
			// PD-16573 — FORM_SUBMITTED and FORM_ACTION_FAILED open the submission.
			return `/forms/submissions/${entityId}`;
		case 'client_visit':
			// PD-17107 — a shared visit debrief opens the visit.
			return `/clients/visits/${entityId}`;
		case 'task':
			// Tasks have no detail route; the list resolves the occurrence
			// (taskId) from this query param (mirrors the comms-panel pattern).
			return `/tasks?taskId=${entityId}`;
		case 'chat_conversation':
			// @-mention notifications deep-link into the conversation via the
			// Communications inbox, which auto-selects the conversation from
			// this query param (mirrors the comms-panel unread-chat rows).
			return `/communications-inbox?selectedConversationId=${entityId}`;
		case 'call':
			// Missed-call notifications deep-link into the call via the
			// Communications inbox, which auto-selects the call row from this
			// query param (mirrors the chat_conversation deep-link above).
			return `/communications-inbox?selectedCallId=${entityId}`;
		case 'transaction':
			// The entity id producers carry is the transaction SUMMARY id,
			// which is what `/transactions/:id` resolves — the raw
			// m_payment_transaction id would 404 here.
			return `/transactions/${entityId}`;
		case 'payment_schedule_occurrence':
			// PD-15867 — occurrences have no detail page by design; the
			// pending cycle charges queue is where one is acted on.
			return '/payments/cycle-charges';
		case 'automation_execution':
			// PD-16942 — an integration sync failure opens the automation run
			// it happened in, which lists each step with its error.
			return `/automation/executions/${entityId}`;
		default:
			return null;
	}
}

export interface NotificationsPanelProps {
	isOpen: boolean;
	onClose: () => void;
	/** Where the notifications come from. Defaults to the CRM user's own. */
	source?: NotificationsDataSourceModel;
	/** Maps a row's (entityType, entityId) to a route. Defaults to the CRM routes. */
	entityPathResolver?: (
		entityType: string | null,
		entityId: number | null,
	) => string | null;
	/** Icon for a row. Defaults to the CRM notification icons. */
	iconResolver?: (type: string, category: string) => IconDefinition;
	/** Route of the settings link in the header; null hides it. Defaults to the CRM's. */
	settingsPath?: string | null;
	/** Accessible name of the settings link. */
	settingsLabel?: string;
	palette?: Partial<NotificationsPanelPaletteModel>;
	/** Anchors the popover below the trigger (top-bar use). */
	anchorRect?: DOMRect | null;
	/**
	 * The badge's unread count, when the caller has it. "Mark all as read"
	 * shows whenever this is above zero, even if every row loaded so far is
	 * read (the newest-first view can hold older unread rows further down).
	 */
	unreadCount?: number;
	/** Called after any mutation so the bell badge re-pulls. */
	onChanged?: () => void;
	/** Navigate to a notification's source. Caller wires this to the
	 *  app navigator (TopBar → NavigationEventService). */
	onNavigate?: (path: string) => void;
	/**
	 * Lets the parent button expose this panel's imperative `fetchList`
	 * to the chat realtime seam (PD-14294) so a live notification nudge
	 * can refresh the in-tray list while it's open. Set on mount, cleared
	 * on unmount.
	 */
	fetchListRef?: React.RefObject<(() => void | Promise<void>) | null>;
}

/**
 * Notification bell tray (DD.9.5.1). The transient-alerts counterpart
 * to the Communications panel: order approvals, @-mentions, system
 * alerts. Read model (matches GitHub / Linear / Slack):
 *
 *   - The badge counts UNREAD; opening the tray does NOT clear it.
 *   - Clicking a row marks it read AND navigates to the source.
 *   - "Mark all as read" clears the badge.
 *
 * A source with `unreadOnlyPreference` (the CRM, PD-14374) has no clearing at
 * all, only read and unread: an "Unread only" toggle (on by default and
 * remembered per user) hides read rows, the per-row dot marks a row read or
 * unread without opening it, and the full view lists newest first and loads
 * older pages as the user scrolls. Any other source (the storefront) keeps the
 * first page grouped into "New" / "Earlier" plus its per-row × dismiss.
 *
 * Built to mirror `CommunicationsPanel` (anchored fixed popover +
 * click-outside catcher, surfaceElevated surface, slow-closed /
 * fast-open poll split) so the two top-bar trays read as one family.
 */
export function NotificationsPanel({
	isOpen,
	onClose,
	source = crmNotificationsSource,
	entityPathResolver = resolveEntityPath,
	iconResolver = iconForNotification,
	settingsPath = CRM_SETTINGS_PATH,
	settingsLabel = 'Notification preferences',
	palette: paletteOverrides,
	anchorRect,
	unreadCount,
	onChanged,
	onNavigate,
	fetchListRef,
}: NotificationsPanelProps): React.JSX.Element | null {
	const { t } = useTranslation();
	const palette = usePanelPalette(paletteOverrides);
	const toggleId = useId();
	const unreadOnlyPreference = source.unreadOnlyPreference;
	// The paged, filterable view (PD-14374) is the CRM's; any other source
	// keeps the original first-page list.
	const isPagedView = unreadOnlyPreference != null;
	const [notifications, setNotifications] = useState<
		ReadonlyArray<NotificationsPanelItemModel>
	>([]);
	const [loading, setLoading] = useState(true);
	const [unreadOnly, setUnreadOnly] = useState<boolean>(
		() => trayUnreadOnlyBySource.get(source.key) ?? true,
	);
	const [hasMore, setHasMore] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	// Mirrors of the state above for the async handlers, so a refresh started
	// by the poll or the realtime nudge always reads the current view and row
	// count without re-creating `fetchList` (which the parent holds by ref).
	const notificationsRef = useRef(notifications);
	notificationsRef.current = notifications;
	const unreadOnlyRef = useRef(unreadOnly);
	unreadOnlyRef.current = unreadOnly;
	const hasMoreRef = useRef(hasMore);
	hasMoreRef.current = hasMore;
	const loadingMoreRef = useRef(false);
	// Guard against setState after the bell (and thus this panel) unmounts
	// mid-fetch — e.g. navigating away while a list pull is in flight.
	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	/**
	 * Reload a view from the top, re-reading at least `count` rows so older
	 * pages the user already scrolled to stay in place. Drops the result if
	 * the user switched views while it was in flight.
	 */
	const reload = useCallback(
		async (view: boolean, count: number): Promise<void> => {
			try {
				if (!isPagedView) {
					const rows = await source.list({ limit: LIST_LIMIT });
					if (isMountedRef.current) setNotifications(rows);
					return;
				}
				const result = await loadLeadingRows(
					source,
					view,
					Math.max(LIST_LIMIT, count),
				);
				if (!isMountedRef.current || unreadOnlyRef.current !== view) {
					return;
				}
				setNotifications(result.rows);
				setHasMore(result.hasMore);
			} catch {
				// Non-fatal — leave the last list in place.
			} finally {
				// A reload for a view the user has since switched away from must
				// not end the new view's loading state early.
				if (isMountedRef.current && unreadOnlyRef.current === view) {
					setLoading(false);
				}
			}
		},
		[source, isPagedView],
	);

	const fetchList = useCallback(
		(): Promise<void> =>
			reload(unreadOnlyRef.current, notificationsRef.current.length),
		[reload],
	);

	/**
	 * The user's remembered view: the session cache, else the server. A failed
	 * read falls back to the default (unread only) without caching it, so the
	 * next open asks again.
	 */
	const resolveUnreadOnly = useCallback(async (): Promise<boolean> => {
		if (unreadOnlyPreference == null) {
			return false;
		}
		const cached = trayUnreadOnlyBySource.get(source.key);
		if (cached != null) {
			return cached;
		}
		try {
			const value = await unreadOnlyPreference.get();
			// The user may have flipped the toggle while this was loading; their
			// choice (already cached and being saved) wins over the stale read.
			const flippedMeanwhile = trayUnreadOnlyBySource.get(source.key);
			if (flippedMeanwhile != null) {
				return flippedMeanwhile;
			}
			trayUnreadOnlyBySource.set(source.key, value);
			return value;
		} catch {
			return trayUnreadOnlyBySource.get(source.key) ?? true;
		}
	}, [source.key, unreadOnlyPreference]);

	useEffect(() => {
		if (!isOpen) {
			// Reopen from the first page rather than re-reading every older page
			// loaded last time.
			setLoading(true);
			setNotifications([]);
			setHasMore(false);
			return;
		}
		let cancelled = false;
		void (async (): Promise<void> => {
			if (isPagedView) {
				const view = await resolveUnreadOnly();
				if (cancelled) {
					return;
				}
				unreadOnlyRef.current = view;
				setUnreadOnly(view);
			}
			await fetchList();
		})();
		const intervalId = setInterval(() => {
			void fetchList();
		}, PANEL_DATA_POLL_MS);
		return () => {
			cancelled = true;
			clearInterval(intervalId);
		};
	}, [isOpen, isPagedView, fetchList, resolveUnreadOnly]);

	// Expose fetchList to the parent button so the chat realtime seam can
	// refresh the in-tray list when a live nudge lands while open (PD-14294).
	useEffect(() => {
		if (fetchListRef == null) {
			return;
		}
		fetchListRef.current = fetchList;
		return (): void => {
			fetchListRef.current = null;
		};
	}, [fetchListRef, fetchList]);

	// Escape closes the tray (keyboard parity with the click-outside catcher).
	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [isOpen, onClose]);

	const { unread, read } = useMemo(() => {
		const unreadRows: Array<NotificationsPanelItemModel> = [];
		const readRows: Array<NotificationsPanelItemModel> = [];
		for (const n of notifications) {
			if (n.isRead) readRows.push(n);
			else unreadRows.push(n);
		}
		return { unread: unreadRows, read: readRows };
	}, [notifications]);

	// Flip the "Unread only" view: switch now, remember it for the session,
	// and save it to the user's account so it follows them. A failed save is
	// non-fatal; the view still switches for this session.
	const handleToggleUnreadOnly = useCallback(
		(next: boolean): void => {
			if (unreadOnlyPreference == null) {
				return;
			}
			unreadOnlyRef.current = next;
			setUnreadOnly(next);
			trayUnreadOnlyBySource.set(source.key, next);
			setLoading(true);
			setNotifications([]);
			setHasMore(false);
			void reload(next, 0);
			void unreadOnlyPreference.set(next).catch(() => {
				/* non-fatal */
			});
		},
		[source.key, unreadOnlyPreference, reload],
	);

	// Load the next older page (the paged view only). One request at a time.
	const loadMore = useCallback(async (): Promise<void> => {
		if (!isPagedView || loadingMoreRef.current || !hasMoreRef.current) {
			return;
		}
		loadingMoreRef.current = true;
		setLoadingMore(true);
		const view = unreadOnlyRef.current;
		try {
			const page = await source.list({
				limit: LIST_LIMIT,
				offset: notificationsRef.current.length,
				unreadOnly: view,
			});
			if (!isMountedRef.current || unreadOnlyRef.current !== view) {
				return;
			}
			setNotifications((prev) => appendNewRows(prev, page));
			setHasMore(page.length === LIST_LIMIT);
		} catch {
			/* non-fatal */
		} finally {
			loadingMoreRef.current = false;
			if (isMountedRef.current) setLoadingMore(false);
		}
	}, [source, isPagedView]);

	const handleListScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>): void => {
			if (!isPagedView) {
				return;
			}
			const el = e.currentTarget;
			if (
				el.scrollHeight - el.scrollTop - el.clientHeight <=
				LOAD_MORE_THRESHOLD_PX
			) {
				void loadMore();
			}
		},
		[isPagedView, loadMore],
	);

	// Click a row: mark read (so it leaves the badge + "New"), then
	// navigate to its source if it has one. Best-effort mutation — a
	// failure still navigates.
	const handleOpenItem = useCallback(
		async (n: NotificationsPanelItemModel): Promise<void> => {
			if (!n.isRead) {
				try {
					await source.markRead(n.id);
				} catch {
					/* non-fatal */
				}
			}
			const path = entityPathResolver(n.entityType, n.entityId);
			onChanged?.();
			onClose();
			if (path != null) onNavigate?.(path);
		},
		[source, entityPathResolver, onChanged, onClose, onNavigate],
	);

	// The per-row dot (PD-14374): mark read or unread without opening the
	// row. Updated in place first so the click lands at once; in the
	// "Unread only" view a row just read leaves the list.
	const markUnreadFromSource = source.markUnread;
	const handleToggleRead = useCallback(
		async (n: NotificationsPanelItemModel): Promise<void> => {
			if (markUnreadFromSource == null) {
				return;
			}
			const nextIsRead = !n.isRead;
			setNotifications((prev) =>
				nextIsRead && unreadOnlyRef.current
					? prev.filter((row) => row.id !== n.id)
					: prev.map((row) =>
							row.id === n.id ? { ...row, isRead: nextIsRead } : row,
						),
			);
			try {
				if (nextIsRead) {
					await source.markRead(n.id);
				} else {
					await markUnreadFromSource(n.id);
				}
			} catch {
				/* non-fatal — the refresh below restores the server's state */
			}
			onChanged?.();
			void fetchList();
		},
		[source, markUnreadFromSource, onChanged, fetchList],
	);

	const dismissFromSource = source.dismiss;
	const handleDismiss = useCallback(
		async (
			e: React.MouseEvent,
			n: NotificationsPanelItemModel,
		): Promise<void> => {
			// Don't trigger the row's open handler.
			e.stopPropagation();
			if (dismissFromSource == null) {
				return;
			}
			try {
				await dismissFromSource(n.id);
			} catch {
				/* non-fatal */
			}
			onChanged?.();
			void fetchList();
		},
		[dismissFromSource, onChanged, fetchList],
	);

	const handleMarkAllRead = useCallback(async (): Promise<void> => {
		try {
			await source.markAllRead();
		} catch {
			// Say so (PD-17025): a failure that only refreshed the list looked
			// exactly like a click that did nothing. The refresh below still
			// runs, so the tray shows whatever the server did manage.
			Toast.show({
				type: 'error',
				text1: t('Could not mark notifications as read'),
			});
		}
		onChanged?.();
		void fetchList();
	}, [source, onChanged, fetchList, t]);

	const popoverStyle = useMemo((): React.CSSProperties => {
		if (anchorRect == null) {
			return { top: '4rem', right: '1rem' };
		}
		return {
			top: anchorRect.bottom + 8,
			right: Math.max(8, window.innerWidth - anchorRect.right),
		};
	}, [anchorRect]);

	if (!isOpen) return null;

	const hasUnread = unread.length > 0;
	const showMarkAllRead = hasUnread || (unreadCount != null && unreadCount > 0);
	const isEmpty = notifications.length === 0;
	const renderRow = (n: NotificationsPanelItemModel): React.JSX.Element => (
		<NotificationRow
			key={n.id}
			notification={n}
			icon={iconResolver(n.notificationType, n.category)}
			palette={palette}
			onOpen={handleOpenItem}
			onToggleRead={
				markUnreadFromSource != null ? handleToggleRead : undefined
			}
			onDismiss={dismissFromSource != null ? handleDismiss : undefined}
		/>
	);

	return (
		<>
			{/* Click-outside catcher */}
			<div className="fixed inset-0 z-30" onClick={onClose} aria-hidden />
			<div
				role="dialog"
				aria-label={t('Notifications')}
				className="fixed z-40 w-96 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-6rem)] overflow-hidden rounded-lg border shadow-xl flex flex-col"
				style={{
					...popoverStyle,
					borderColor: palette.border,
					backgroundColor: palette.surface,
				}}
			>
				<div className="border-b" style={{ borderColor: palette.border }}>
					<div className="px-4 py-3 flex items-center justify-between gap-2">
						<span
							className="font-semibold text-sm"
							style={{ color: palette.textPrimary }}
						>
							{t('Notifications')}
						</span>
						<div className="flex items-center gap-3">
							{showMarkAllRead && (
								<button
									type="button"
									onClick={() => void handleMarkAllRead()}
									className="text-xs hover:opacity-80"
									style={{ color: palette.link }}
								>
									{t('Mark all as read')}
								</button>
							)}
							{settingsPath != null && (
								<button
									type="button"
									onClick={() => {
										onClose();
										onNavigate?.(settingsPath);
									}}
									className="text-xs hover:opacity-80"
									style={{ color: palette.textSecondary }}
									aria-label={t(settingsLabel)}
								>
									<FontAwesomeIcon icon={faGear} aria-hidden />
								</button>
							)}
							<button
								type="button"
								onClick={onClose}
								className="text-xs hover:opacity-80"
								style={{ color: palette.textSecondary }}
								aria-label={t('Close')}
							>
								{t('Close')}
							</button>
						</div>
					</div>
					{isPagedView && (
						<div className="px-4 pb-3">
							<Toggle
								id={toggleId}
								checked={unreadOnly}
								onChange={handleToggleUnreadOnly}
								label={t('Unread only')}
							/>
						</div>
					)}
				</div>

				<div className="flex-1 overflow-y-auto" onScroll={handleListScroll}>
					{loading ? (
						<div
							className="px-4 py-6 text-center text-xs italic"
							style={{ color: palette.textSecondary }}
						>
							{t('Loading…')}
						</div>
					) : isEmpty ? (
						<NotificationsEmptyState palette={palette} />
					) : isPagedView ? (
						<div className="px-2 py-2">
							<div className="flex flex-col gap-1">
								{notifications.map(renderRow)}
							</div>
							{loadingMore ? (
								<div
									className="px-2 py-3 text-center text-xs italic"
									style={{ color: palette.textSecondary }}
								>
									{t('Loading…')}
								</div>
							) : hasMore ? (
								// Scrolling near the bottom loads the next page on its own;
								// the button covers a list too short to scroll and
								// keyboard users.
								<div className="px-2 py-2 text-center">
									<button
										type="button"
										onClick={() => void loadMore()}
										className="text-xs hover:opacity-80"
										style={{ color: palette.link }}
									>
										{t('Load older notifications')}
									</button>
								</div>
							) : null}
						</div>
					) : (
						<>
							{hasUnread && (
								<NotificationGroup title={t('New')} palette={palette}>
									{unread.map(renderRow)}
								</NotificationGroup>
							)}
							{read.length > 0 && (
								<NotificationGroup title={t('Earlier')} palette={palette}>
									{read.map(renderRow)}
								</NotificationGroup>
							)}
						</>
					)}
				</div>
			</div>
		</>
	);
}

function NotificationGroup({
	title,
	palette,
	children,
}: {
	title: string;
	palette: NotificationsPanelPaletteModel;
	children: React.ReactNode;
}): React.JSX.Element {
	return (
		<div className="px-2 py-2">
			<div
				className="px-2 py-1.5 mb-1 text-xs font-semibold uppercase tracking-wide"
				style={{ color: palette.textSecondary }}
			>
				{title}
			</div>
			<div className="flex flex-col gap-1">{children}</div>
		</div>
	);
}

function NotificationRow({
	notification,
	icon,
	palette,
	onOpen,
	onToggleRead,
	onDismiss,
}: {
	notification: NotificationsPanelItemModel;
	icon: IconDefinition;
	palette: NotificationsPanelPaletteModel;
	onOpen: (n: NotificationsPanelItemModel) => void;
	/**
	 * Present when the source can mark a row unread: the dot becomes a
	 * read / unread button. Otherwise it is a plain unread marker.
	 */
	onToggleRead?: (n: NotificationsPanelItemModel) => void;
	/** Omitted when the source cannot dismiss; the × is then not rendered. */
	onDismiss?: (e: React.MouseEvent, n: NotificationsPanelItemModel) => void;
}): React.JSX.Element {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const n = notification;
	const isUrgent = n.priority === 'urgent';
	// The icon chip takes the badge treatment, so urgency reads from a stroke
	// and the icon inherits its tone rather than restating it.
	const accentChip = isUrgent
		? colors.classes.badgeDanger
		: colors.classes.badgeInfo;
	const readToggleLabel = n.isRead ? t('Mark as unread') : t('Mark as read');

	return (
		<div
			className="flex items-start gap-2.5 rounded px-2 py-2"
			style={{ borderColor: palette.border }}
		>
			{onToggleRead != null ? (
				// Filled dot = unread, empty ring = read. Always drawn (not on
				// hover only) so it works on touch, with a 28px hit area.
				<Tooltip content={readToggleLabel} className="flex-shrink-0">
					<button
						type="button"
						onClick={(e) => {
							// The Tooltip's wrapper toggles it on click; stopping here
							// keeps it open so it names the new action after the flip.
							e.stopPropagation();
							void onToggleRead(n);
						}}
						className="flex items-center justify-center w-7 h-7 -my-0.5 rounded-full hover:opacity-70"
						aria-label={readToggleLabel}
					>
						<span
							className="w-2.5 h-2.5 rounded-full"
							style={
								n.isRead
									? {
											border: `1.5px solid ${palette.textSecondary}`,
											backgroundColor: 'transparent',
										}
									: { backgroundColor: palette.unreadDot }
							}
							aria-hidden
						/>
					</button>
				</Tooltip>
			) : (
				// Unread marker — transparent placeholder keeps rows aligned.
				<span
					className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
					style={{
						backgroundColor: n.isRead ? 'transparent' : palette.unreadDot,
					}}
					aria-hidden
				/>
			)}
			<span
				className={cn(
					'flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0',
					accentChip,
				)}
			>
				<FontAwesomeIcon icon={icon} className="text-xs" aria-hidden />
			</span>
			<button
				type="button"
				onClick={() => onOpen(n)}
				className="flex-1 min-w-0 text-left"
			>
				<div className="flex items-baseline justify-between gap-2">
					<span
						className="text-sm truncate"
						style={{
							color: palette.textPrimary,
							fontWeight: n.isRead ? 400 : 500,
						}}
					>
						{t(n.title)}
					</span>
					<span
						className="text-xs flex-shrink-0"
						style={{ color: palette.textSecondary }}
					>
						{t(formatRelativeTime(n.createdDate))}
					</span>
				</div>
				{n.body != null && n.body !== '' && (
					<p
						className="text-xs truncate mt-0.5"
						style={{ color: palette.textSecondary }}
					>
						{t(n.body)}
					</p>
				)}
			</button>
			{onDismiss != null && (
				<button
					type="button"
					onClick={(e) => void onDismiss(e, n)}
					className="flex-shrink-0 opacity-60 hover:opacity-100"
					style={{ color: palette.textSecondary }}
					aria-label={t('Dismiss notification')}
				>
					<FontAwesomeIcon icon={faXmark} className="text-xs" aria-hidden />
				</button>
			)}
		</div>
	);
}

function NotificationsEmptyState({
	palette,
}: {
	palette: NotificationsPanelPaletteModel;
}): React.JSX.Element {
	const { t } = useTranslation();
	return (
		<div className="flex flex-col items-center justify-center text-center px-6 py-12 gap-2">
			<span
				className="flex items-center justify-center w-12 h-12 rounded-full"
				style={{ backgroundColor: palette.surface }}
			>
				<FontAwesomeIcon
					icon={faBellSlash}
					style={{ color: palette.textSecondary }}
					aria-hidden
				/>
			</span>
			<span
				className="text-sm font-medium"
				style={{ color: palette.textPrimary }}
			>
				{t("You're all caught up")}
			</span>
			<span className="text-xs" style={{ color: palette.textSecondary }}>
				{t('New notifications will appear here.')}
			</span>
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────
// Indicator hook — polls the unread count at a slow cadence even when
// the tray is closed, so the bell badge stays current.
// ─────────────────────────────────────────────────────────────────

/**
 * PD-16055 — ONE poll for the unread count, however many bells are mounted.
 * The TopBar renders its desktop controls and its mobile bottom bar at the
 * same time (CSS hides one), so this hook runs twice on every page; owning
 * the interval per hook instance meant two requests on load and two every
 * minute thereafter, for the life of the session.
 */
export const notificationsUnreadPoll = createSharedPoll(
	async () => (await NotificationQueryService.getUnreadCount()).count,
	INDICATOR_POLL_MS,
	0,
);

/**
 * One shared unread poll per data source (PD-16475). The CRM keeps the poll
 * above (the shell session reset clears it); another source gets its own the
 * first time a bell asks, so the CRM and storefront counts can never mix.
 */
const unreadPollsBySource = new Map<string, SharedPoll<number>>([
	[crmNotificationsSource.key, notificationsUnreadPoll],
]);

/**
 * The shared unread poll for a source — also how an app resets its count on
 * sign-out (`getNotificationsUnreadPoll(source).reset()`).
 */
export function getNotificationsUnreadPoll(
	source: NotificationsDataSourceModel,
): SharedPoll<number> {
	let poll = unreadPollsBySource.get(source.key);
	if (poll == null) {
		poll = createSharedPoll(
			() => source.getUnreadCount(),
			INDICATOR_POLL_MS,
			0,
		);
		unreadPollsBySource.set(source.key, poll);
	}
	return poll;
}

export function useNotificationsIndicator(
	source: NotificationsDataSourceModel = crmNotificationsSource,
): {
	unreadCount: number;
	refresh: () => Promise<void>;
} {
	const poll = getNotificationsUnreadPoll(source);
	const [unreadCount, setUnreadCount] = useState(poll.getSnapshot);

	useEffect(() => poll.subscribe(setUnreadCount), [poll]);

	return { unreadCount, refresh: poll.refresh };
}

// ─────────────────────────────────────────────────────────────────
// Top-bar trigger button + badge + panel-mount.
// ─────────────────────────────────────────────────────────────────

/** The bell button's props: the panel's data and look, plus the button's own. */
export interface NotificationsPanelButtonProps
	extends Pick<
		NotificationsPanelProps,
		| 'source'
		| 'entityPathResolver'
		| 'iconResolver'
		| 'settingsPath'
		| 'settingsLabel'
		| 'palette'
	> {
	onNavigate?: (path: string) => void;
	/**
	 * Replaces the button's classes (size, shape, text color) so it can sit on
	 * a surface other than the CRM top bar. When set, the default inline text
	 * color is dropped so the class can color the icon.
	 */
	buttonClassName?: string;
}

/**
 * Drop-in replacement for the TopBar's Notifications bell stub. Owns
 * the tray's open/close state, shows the unread count badge, and
 * positions the popover under itself — mirroring
 * `CommunicationsPanelButton`. The storefront mounts the same button over
 * its own data source (PD-16475).
 */
export function NotificationsPanelButton({
	onNavigate,
	source = crmNotificationsSource,
	entityPathResolver,
	iconResolver,
	settingsPath,
	settingsLabel,
	palette: paletteOverrides,
	buttonClassName,
}: NotificationsPanelButtonProps): React.JSX.Element {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const palette = usePanelPalette(paletteOverrides);
	const [isOpen, setIsOpen] = useState(false);
	const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const indicator = useNotificationsIndicator(source);
	const refreshIndicator = indicator.refresh;

	// Filled by the panel; lets the realtime seam refresh the in-tray list
	// when a nudge lands while the tray is open.
	const fetchListRef = useRef<(() => void | Promise<void>) | null>(null);
	// `isOpen` read through a ref so the registered handle (registered once on
	// mount) always sees the live open state without re-registering on toggle.
	const isOpenRef = useRef(isOpen);
	isOpenRef.current = isOpen;

	// Register the bell's imperative refreshers with the chat realtime seam
	// (PD-14294) so a live "notification" nudge refreshes the badge (and the
	// list when open) within ~1s — the 60s/30s polls remain the fallback.
	useEffect(() => {
		const unregister = registerIndicator({
			refresh: refreshIndicator,
			fetchList: () => fetchListRef.current?.(),
			isPanelOpen: () => isOpenRef.current,
		});
		return unregister;
	}, [refreshIndicator]);

	const handleToggle = useCallback((): void => {
		const rect = buttonRef.current?.getBoundingClientRect() ?? null;
		setAnchorRect(rect);
		setIsOpen((prev) => !prev);
	}, []);

	const badgeLabel =
		indicator.unreadCount > BADGE_CAP
			? `${BADGE_CAP}+`
			: String(indicator.unreadCount);

	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				onClick={handleToggle}
				className={
					buttonClassName ??
					'relative flex items-center justify-center w-10 h-10 rounded-full transition-colors hover:opacity-80'
				}
				style={
					buttonClassName == null
						? { color: colors.values.textSecondary }
						: undefined
				}
				aria-label={t('Notifications')}
				aria-expanded={isOpen}
				title={t('Notifications')}
			>
				<FontAwesomeIcon icon={faBell} className="w-4 h-4" aria-hidden />
				{indicator.unreadCount > 0 && (
					<span
						className={cn(
							'absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[1rem] h-4 px-1 rounded-full text-[10px] font-semibold leading-none',
						)}
						style={{
							backgroundColor: colors.values.danger,
							color: '#ffffff',
							// Ring matches the bar surface so the badge reads in any theme.
							boxShadow: `0 0 0 2px ${palette.badgeRing}`,
						}}
						aria-label={t('{count} unread notifications').replace(
							'{count}',
							String(indicator.unreadCount),
						)}
					>
						{t(badgeLabel)}
					</span>
				)}
			</button>
			<NotificationsPanel
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				source={source}
				entityPathResolver={entityPathResolver}
				iconResolver={iconResolver}
				settingsPath={settingsPath}
				settingsLabel={settingsLabel}
				palette={paletteOverrides}
				anchorRect={anchorRect}
				unreadCount={indicator.unreadCount}
				onChanged={refreshIndicator}
				onNavigate={onNavigate}
				fetchListRef={fetchListRef}
			/>
		</>
	);
}
